import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { apiService, type Toy as ToyType } from "../services/api";
import {
  Section,
  Container,
  Heading,
  Card,
  CardInner,
  Button,
  Carousel,
} from "toddlar-ui";
import ToyCard from "../components/ToyCard";
import { useAuth } from "../state/auth";
import { useOrder } from "../state/order";
import OrderModal from "../modals/orderModal";
import Rating from "../components/Rating";
import TextBlock from "../components/TextBlock";

const ToyPage: React.FC = () => {
  const { name } = useParams<{ name: string }>();
  const { token } = useAuth();
  const { add } = useOrder();
  const navigate = useNavigate();
  const [toy, setToy] = useState<ToyType | null>(null);
  const [related, setRelated] = useState<ToyType[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>("");
  const [orderOpen, setOrderOpen] = useState(false);
  const [justAdded, setJustAdded] = useState<{
    toy: ToyType;
    quantity?: number;
  } | null>(null);

  console.log(toy);

  // Ensure page scrolls to top on load / when toy changes
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [name]);

  useEffect(() => {
    const run = async () => {
      try {
        setLoading(true);
        setError("");
        // We don't have a slug endpoint; query all and find by name for now
        const list = await apiService.getToys({ limit: 200 });
        if (list.success && list.data) {
          const decoded = decodeURIComponent(name || "");
          const found = list.data.toys.find(
            (t) => t.name.toLowerCase() === decoded.toLowerCase()
          );
          if (!found) {
            setError("Toy not found");
          } else {
            setToy(found);
            const others = list.data.toys
              .filter((x) => x._id !== found._id)
              .sort(() => 0.5 - Math.random())
              .slice(0, 3);
            setRelated(others);
          }
        } else {
          setError("Failed to load toy");
        }
      } catch (e) {
        setError(e instanceof Error ? e.message : "Failed to load toy");
      } finally {
        setLoading(false);
      }
    };
    run();
  }, [name]);

  const images = useMemo(() => {
    if (!toy) return [] as string[];
    return Array.isArray(toy.images) ? toy.images : [];
  }, [toy]);

  if (loading) {
    return (
      <div className="min-h-screen bg-sand-50 flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-brand-600 mb-4"></div>
          <p className="text-ink-muted">Loading toy...</p>
        </div>
      </div>
    );
  }

  if (error || !toy) {
    return (
      <div className="min-h-screen bg-sand-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-ink-muted mb-4">{error || "Toy not found"}</p>
          <Button onClick={() => navigate("/browse")} size="large">
            Back to Browse
          </Button>
        </div>
      </div>
    );
  }

  const openOrderModal = () => {
    setOrderOpen(true);
    setTimeout(() => {
      setOrderOpen(false);
    }, 3000);
  };

  return (
    <div className="min-h-screen bg-sand-50">
      <Section>
        <Container>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div>
              <Carousel images={images} />
            </div>
            <div>
              <Card>
                <CardInner>
                  <Heading size="h2" className="mb-2">
                    {toy.name}
                  </Heading>
                  <div className="mb-2">
                    <Rating value={toy.rating ?? 0} />
                  </div>
                  <div className="mt-4 text-ink">
                    <span className="text-2xl font-bold text-brand-600">
                      {toy.points}
                    </span>{" "}
                    points
                  </div>

                  <div className="mt-6">
                    {token ? (
                      <Button
                        size="large"
                        onClick={() => {
                          add(toy);
                          setJustAdded({ toy, quantity: 1 });
                          openOrderModal();
                        }}
                      >
                        Add to order
                      </Button>
                    ) : (
                      <Button size="large" onClick={() => navigate("/login")}>
                        Login to add to order
                      </Button>
                    )}
                  </div>
                </CardInner>
              </Card>
            </div>
          </div>
        </Container>
      </Section>

      <Section>
        <Container>
          <Card>
            <CardInner>
              <Heading size="h2" className="mb-3">
                Product information
              </Heading>
              <TextBlock text={toy.description} className="text-ink" />
            </CardInner>
          </Card>
        </Container>
      </Section>

      <Section>
        <Container>
          <Heading size="h2" className="mb-4">
            Related products
          </Heading>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {related.map((r) => (
              <ToyCard key={r._id} toy={r} />
            ))}
          </div>
        </Container>
      </Section>

      <OrderModal
        open={orderOpen}
        onClose={() => setOrderOpen(false)}
        justAdded={justAdded}
      />
    </div>
  );
};

export default ToyPage;
